'use client';

import { memo, useEffect, useRef, useState, type RefObject } from 'react';
import { Star } from 'lucide-react';
import type { Media } from '../../../payload-types';
import type { SubjectAttributes } from '../../../utilities/change-requests/subject-marks';
import { cn } from '../../../utilities/classname';
import { Reveal } from '../reveal';
import type { SubjectsById } from '../subjects-by-id';
import { PortfolioComparison } from './portfolio-comparison';
import { StripArrows } from './strip-arrows';
import { usePortfolioSliders } from './use-portfolio-sliders';

/**
 * One item of the showcase.
 *
 * A structural shape, deliberately NOT an import of `src/api`'s `PortfolioCard`:
 * a component under `shared/` must not reach into the data layer, the same rule
 * `PortfolioFilmstripItem` keeps next door. `PortfolioCard` satisfies this —
 * this style reads every field of it, which is why this shape is the whole of
 * that type and the strip's is narrower.
 */
export type PortfolioShowcaseItem = {
  id: string;
  title: string;
  summary: string | null;
  /** The town the work happened in, shown ahead of the date in the meta line. */
  place: string | null;
  completedLabel: string | null;
  /** The comparison pair. Both null renders {@link PortfolioShowcaseItem.image} alone. */
  before: Media | null;
  after: Media | null;
  image: Media;
  quote: string | null;
  reviewer: string | null;
  rating: number | null;
};

/** Two digits, so "01" stays monospaced-aligned down the rail and in the phone counter. */
const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * The rail label: the item title up to its first comma.
 *
 * Portfolio titles are written "what, where" ("Boiler room replacement,
 * Brighton seafront hotel"), so the head alone is the job and the tail is
 * location the rail does not have room for. A title with no comma is already
 * short enough and passes through whole.
 */
const railLabel = (title: string): string => title.split(',')[0].trim();

/**
 * Centres the rail's `index`th button in the RAIL's own scrollport.
 *
 * Below `lg` the rail is a horizontal scroller of `w-40` buttons — six of them
 * are ~960px inside a 360px phone — so stepping with the arrows would otherwise
 * leave the highlighted button off-screen, and the one thing that shows the
 * visitor where they are would silently disagree with the photo.
 *
 * Scrolls the RAIL, never `scrollIntoView`: that walks up every scrollable
 * ancestor, so a rail sitting below the fold would yank the whole page down on
 * an arrow click. Only the row copy scrolls; the side column's vertical list
 * shown from `lg` is a separate copy that never needs to, and the row is
 * `display: none` there, where its zero widths clamp every target to zero.
 *
 * Offsets are measured as a DELTA from the first button rather than read
 * absolutely: `offsetLeft` is relative to the nearest positioned ancestor, which
 * is not necessarily the scroller, and the difference cancels whatever that is.
 *
 * TAKES THE NULLABLE REF VALUE for the same reason `scrollStripBy` does — a ref
 * is null only before the first commit, which is before the arrows exist to be
 * clicked, so a guard inside the component would be unreachable from its own
 * tests. Here it is an ordinary argument.
 */
export const scrollRailTo = (el: HTMLElement | null, index: number): void => {
  if (el === null) { return; }

  const [first, target] = [el.children[0], el.children[index]];

  if (!(first instanceof HTMLElement) || !(target instanceof HTMLElement)) { return; }

  const start = target.offsetLeft - first.offsetLeft;
  const left = start - (el.clientWidth - target.clientWidth) / 2;
  const max = el.scrollWidth - el.clientWidth;

  el.scrollTo({ left: Math.max(0, Math.min(max, left)), behavior: 'smooth' });
};

/**
 * Whether the slide on show fits in the viewport below its own sticky `top`.
 *
 * The slide sticks beside a long index so the job being chosen stays in view,
 * but a slide TALLER than the viewport would stick with its bottom (the plate's
 * quote and reviewer) below the fold for the whole sticky range, out of reach
 * until the list ends. So it sticks only when it fits: the slide's height plus
 * its resolved `top` (`top-below-header-8`, which includes a sticky site
 * header's inset) against the window's height. Re-measured when the slide on
 * show changes, when it resizes (images load, fonts swap) and when the window
 * does. Until the first measurement the slide is not sticky, which is also
 * what the server renders.
 *
 * Keyed on `items` as well as `index`: the list is a server prop and can change
 * under a mounted showcase, which puts a different element on show without the
 * index moving, and a measurement bound to the old one would keep deciding from
 * a slide gone from the page.
 */
export const useSlideFitsViewport = (
  layoutRef: RefObject<HTMLDivElement | null>,
  index: number,
  items: PortfolioShowcaseItem[],
): boolean => {
  const [fits, setFits] = useState(false);

  useEffect(() => {
    // The slide on show: the one direct child marked as a slide and not hidden.
    const slide = Array.from(layoutRef.current?.children ?? [])
      .find((child): child is HTMLElement => child instanceof HTMLElement && 'slide' in child.dataset && !child.hidden);

    if (slide === undefined) { return undefined; }

    const measure = () => {
      const top = Number.parseFloat(getComputedStyle(slide).top);

      setFits(slide.offsetHeight + (Number.isNaN(top) ? 0 : top) <= window.innerHeight);
    };

    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(slide);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [layoutRef, index, items]);

  return fits;
};

/**
 * Re-centres the row rail on the active item whenever the row goes from hidden
 * to shown.
 *
 * The row is `display: none` from `lg`, where the side column's list does its
 * job, so a step taken there cannot scroll it ({@link scrollRailTo} reads zero
 * widths and does nothing). Narrow the window or turn a tablet upright
 * afterwards and the row reappears scrolled wherever it was left, the active
 * item off its edge. Only the hidden-to-shown transition re-centres: a resize
 * of a row already on screen leaves a rail the visitor scrolled by hand alone.
 *
 * Keyed on `items`, because the row mounts only once there is more than one
 * item: a list that grows under a mounted showcase gets a row the first render
 * never saw.
 */
export const useRecentreRailWhenShown = (
  railRef: RefObject<HTMLDivElement | null>,
  index: number,
  items: PortfolioShowcaseItem[],
): void => {
  const indexRef = useRef(index);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    const rail = railRef.current;

    if (rail === null || typeof ResizeObserver === 'undefined') { return undefined; }

    let wasHidden = rail.clientWidth === 0;
    const observer = new ResizeObserver(() => {
      const hidden = rail.clientWidth === 0;

      if (wasHidden && !hidden) { scrollRailTo(rail, indexRef.current); }

      wasHidden = hidden;
    });

    observer.observe(rail);

    return () => observer.disconnect();
  }, [railRef, items]);
};

/**
 * The lone photo's box when there is no side column: the two thirds it would
 * take beside one, centred. The view-all link under it takes the same box, so
 * it lines up with the photo rather than the container edge.
 */
const CENTRED_SLIDE_CLASS = 'lg:mx-auto lg:w-2/3';

const StarRow: React.FC<{ rating: number }> = ({ rating }) => {
  // Never trust a stored rating to be in range: an out-of-range value would
  // otherwise decide the fill of a fixed five-icon row nonsensically.
  const filled = Math.min(5, Math.max(0, rating));

  return (
    <span
      role="img"
      aria-label={`Rated ${filled} out of 5`}
      className="flex items-center gap-0.5"
    >
      {Array(5).fill(0).map((_, i) => (
        <Star
          key={i}
          className={cn('size-4', i < filled ? 'fill-warning text-warning' : 'text-base-content/20')}
        />
      ))}
    </span>
  );
};

/**
 * The index rail, in one of its two layouts. {@link PortfolioShowcase} renders
 * it TWICE, as it does the arrows: a scrolling ROW under the photo below `lg`,
 * and a vertical COLUMN in the side column from `lg`. Each copy is
 * `display: none` at the other's widths, which takes it out of the tab order
 * and the accessibility tree, so no width ever has two.
 *
 * Twice, rather than one rail moved between the two places with CSS, because
 * one rail cannot sit in the right place in the DOM for both: beside the photo
 * from `lg` it belongs in the side column, ahead of the slides; under the photo
 * below `lg` it belongs after them. One element moved with `display: contents`
 * and `order-last` was drawn under the photo on a phone while it was READ ahead
 * of it, so Tab went arrows → the rail below the fold → back up to the photo's
 * slider. The cost is the rail labels twice in the served HTML, which only a
 * search engine reads twice: the markdown and chatbot pipelines
 * (`extractPageContent`) drop every `<button>`, so neither copy reaches them.
 *
 * Below `lg` the row is a scroller of `w-40` buttons, because six columns in a
 * phone's 360px are 60px, which fits neither the number nor a word of the
 * label; `scrollerRef` is for that copy, the one {@link scrollRailTo} keeps the
 * active button in view in. The column simply grows a row per item.
 *
 * THE ROW'S RULE IS ON A WRAPPER, NOT THE SCROLLER. The active marker is meant
 * to lie over the rule, but a scroller clips at its padding edge on BOTH axes
 * (`overflow-x: auto` forces `overflow-y` to clip too), so a button pulled up
 * over the scroller's own border lost that pixel. The scroller is pulled up
 * over the wrapper's rule instead, so its buttons start on the rule, unclipped.
 *
 * The rule, the marker and the hover are drawn from `current`, the section's
 * text colour, so they follow an operator's theme (see `FILMSTRIP_ARROW_CLASS`).
 * The marker is the ACTIVE item's alone: the hover strengthens an idle item's
 * edge as a preview, and on the active one would halve it. Dimmed text stays at
 * 80% or more, the lowest opacity that holds WCAG AA (4.5:1) on the dark band,
 * a `base-100` theme and a `primary` theme alike; at 55% an idle label measured
 * 3.9:1 on white. The focus ring is the site-wide one, whose colour follows the
 * surface (`--focus-ring` in `base.css`); the row draws it 2px INSIDE its
 * buttons, because a scroller clips on both axes and the site-wide ring sits
 * 2px outside.
 */
const ShowcaseRail: React.FC<{
  items: PortfolioShowcaseItem[];
  index: number;
  onSelect: (index: number) => void;
  orientation: 'row' | 'column';
  scrollerRef?: React.Ref<HTMLDivElement>;
}> = ({ items, index, onSelect, orientation, scrollerRef }) => {
  const isRow = orientation === 'row';

  const buttons = items.map((item, i) => (
    <button
      key={item.id}
      type="button"
      onClick={() => onSelect(i)}
      aria-current={i === index}
      className={cn(
        'flex text-left transition-colors duration-200 ease-out hover:bg-current/5',
        isRow
          ? 'w-40 flex-none snap-start flex-col gap-2.5 rounded-b-field border-t-2 px-4 pt-4.5 pb-5.5 focus-visible:-outline-offset-2'
          : '-ml-px items-baseline gap-4 rounded-r-field border-l-2 px-5 py-2.5',
        i === index ? 'border-current' : 'border-transparent hover:border-current/50',
      )}
    >
      <span className="font-mono text-xs tracking-widest opacity-80">{pad(i + 1)}</span>
      <span
        className={cn(
          'text-sm leading-normal transition-opacity duration-200 ease-out',
          i === index ? 'opacity-100' : 'opacity-80',
        )}
      >
        {railLabel(item.title)}
      </span>
    </button>
  ));

  if (!isRow) {
    return <div data-rail="column" className="flex flex-col border-l border-current/20">{buttons}</div>;
  }

  return (
    <div className="border-t border-current/20">
      <div ref={scrollerRef} data-rail="row" className="-mt-px flex overflow-x-auto snap-x snap-proximity">
        {buttons}
      </div>
    </div>
  );
};

/**
 * One item: its photo, and the plate of copy that overlaps it.
 *
 * EVERY ITEM IS RENDERED AND `hidden` DECIDES WHICH ONE IS ON SCREEN. The
 * obvious shape for a stepper is to render `items[index]` alone, which is what
 * this style used to do, and it cost the section every word of every item
 * behind the rail. Googlebot renders JavaScript but never clicks, and the
 * platform's own readers do not even do that much: `crawlPage` fetches a page's
 * HTML and converts it with no JavaScript at all, which is what answers the
 * markdown-for-agents route and what indexes a page into the chatbot's vector
 * store, and `renderPageMarkdown` does the same for the AI page-SEO pass. Every
 * one of them read one job out of six, plus five rail labels cut at their first
 * comma by {@link railLabel}. Five summaries, five quotes and five photos
 * reached none of them. They are all in the HTML now, which is the whole point
 * of this component's shape.
 *
 * THE NATIVE `hidden` ATTRIBUTE — not a `display: none` utility, not
 * `aria-hidden` plus `inert`. One attribute hides the slide AND drops it from
 * the accessibility tree AND takes its comparison slider out of the tab order:
 * a screen reader must not read six jobs as though six were on screen, and five
 * off-screen sliders must not be tab stops. Nothing has to be kept in sync,
 * because there is only one thing to keep. Crawlers index `display: none`
 * content — the reason tabs and accordions have always been safe — so hiding it
 * costs nothing that rendering it was buying.
 *
 * Tailwind's preflight is what makes it stick, and it is stronger than the UA
 * rule it replaces: `[hidden]:where(:not([hidden='until-found']))` is declared
 * `display: none !important`, so the attribute OUTRANKS every `display`
 * utility instead of losing to one. A slide that later grows a `flex` or a
 * `block` class still hides.
 *
 * The reveal animation restarts each time a slide is shown, since an element
 * returning from `display: none` starts its animations again. Where
 * `animation-timeline: view()` is supported that resolves against scroll
 * position, so a section already in view lands at the end of its range and the
 * slide simply appears; the older time-based fallback replays the fade, which
 * reads as a cross-fade between jobs.
 *
 * MEMOISED, and `onPct` takes the item id for that reason. Rendering every item
 * put every slide under one parent, so without this a divider drag — which sets
 * state on every `pointermove` — would re-render all of them, rebuilding the
 * `next/image` props of five slides that cannot have changed on screen. Hidden
 * slides cost no layout or paint, so the waste is JavaScript only; the point is
 * simply not to regress a control whose whole design is about tracking the
 * pointer exactly (see `portfolio-comparison.tsx`). A per-item `onPct` closure
 * built in the parent's `map` would be a fresh reference on every render and
 * defeat the memo, so the parent hands over the stable `setPct` and the id is
 * bound HERE. Every other prop is a primitive, the caller's own item object,
 * or `subjects` or its entry, which keep their references between renders.
 */
const ShowcaseSlide = memo(function ShowcaseSlide({
  item,
  hidden,
  showQuotes,
  showPlace,
  pct,
  onPct,
  sizes,
  centred,
  sticky,
  placeholder,
  subject,
  subjects,
}: {
  item: PortfolioShowcaseItem;
  hidden: boolean;
  showQuotes: boolean;
  showPlace: boolean;
  pct: number;
  /** Takes the id so the parent can hand over one stable callback. */
  onPct: (id: string, pct: number) => void;
  /** The photo's `sizes`, which only the caller's container can say. */
  sizes: string;
  /** No side column to sit beside: the slide centres itself instead. */
  centred: boolean;
  /** Beside the side column and short enough to stick (see `useSlideFitsViewport`). */
  sticky: boolean;
  /** Show the stored blur while the photo loads: the slide on show at load only. */
  placeholder: boolean;
  /** The item's Subject attribute, spread on the slide. */
  subject: SubjectAttributes | undefined;
  /** The section's Subjects, for the slide's photos (by Media id). */
  subjects: SubjectsById | undefined;
}) {
  const place = showPlace ? item.place : null;
  const meta = [place, item.completedLabel].filter((part) => part !== null).join(' · ');
  const quote = showQuotes ? item.quote : null;

  return (
    // The two thirds beside the side column from `lg`. Every slide names the
    // same cell; the hidden ones are `display: none` and take no part in the
    // grid, so only the one on show ever occupies it. With no side column (no
    // heading, one item) there is no grid: the slide keeps the same two-thirds
    // width, centred, rather than sitting beside an empty third.
    //
    // STICKY beside the side column: a long index (up to twelve jobs) runs
    // taller than the photo and plate, and the job being chosen must not scroll
    // away while the visitor reads down the list. `self-start` is what lets it
    // stick, since a grid item stretched to the row's height has nowhere to go;
    // `top-below-header-8` keeps it clear of a sticky site header, and stays on
    // when it does not stick so `useSlideFitsViewport` can read it.
    <div
      hidden={hidden}
      data-slide=""
      {...subject}
      className={centred
        ? CENTRED_SLIDE_CLASS
        : cn('lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:top-below-header-8 lg:self-start', sticky && 'lg:sticky')}
    >
      <Reveal durationMs={700} cover={30}>
        <PortfolioComparison
          variant="showcase"
          before={item.before}
          after={item.after}
          image={item.image}
          pct={pct}
          onPct={(next) => onPct(item.id, next)}
          title={item.title}
          aspect="aspect-video"
          sizes={sizes}
          placeholder={placeholder}
          subjects={subjects}
        />
      </Reveal>

      {/*
        The plate OVERLAPS the photo on wide viewports and becomes a plain
        block beneath it below `lg`. At narrow widths the overlap would eat
        most of a 16:9 photo's height, and the four-fifths width that keeps the
        plate clear of the photo's right edge has no room to be inset.

        Everything the plate hangs below the photo is section height, so from
        `lg` it overlaps by 80px, the title no longer steps up to `text-3xl`
        and the quote steps down to `text-base`. Beside the side column the
        photo is ~730px wide, and the larger sizes wrapped the plate a line or
        two deeper for no gain.
      */}
      <Reveal
        stagger={1}
        durationMs={700}
        cover={35}
        className="lg:-mt-20 lg:ml-8 lg:w-4/5"
      >
        <div className="relative mt-6 flex flex-col gap-3.5 rounded-box bg-base-100 p-6 text-base-content drop-shadow-xl drop-shadow-base-content lg:mt-0 lg:p-9 lg:px-10">
          {meta === '' ? null : (
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">{meta}</p>
          )}

          <h3 className="text-2xl font-bold leading-tight text-balance">{item.title}</h3>

          {item.summary === null ? null : (
            <p className="text-base leading-relaxed text-base-content/75 max-w-[52ch]">{item.summary}</p>
          )}

          {quote === null ? null : (
            <blockquote className="flex flex-col gap-2 pt-1.5">
              {item.rating === null ? null : <StarRow rating={item.rating} />}

              <p className="text-lg lg:text-base italic leading-relaxed max-w-[44ch]">&ldquo;{quote}&rdquo;</p>

              {item.reviewer === null ? null : (
                <footer className="text-xs text-base-content/65 not-italic">{item.reviewer}</footer>
              )}
            </blockquote>
          )}
        </div>
      </Reveal>
    </div>
  );
});

/**
 * Style A — one job at a time, at photographic scale, on the dark band.
 *
 * The market page's showcase style, and one of the homepage's portfolio
 * layouts.
 * It lived beside the market sections while it was the only caller and moved
 * here the moment a second template rendered it — the same move `PortfolioCard`
 * made, for the same reason.
 *
 * ITS CALLER PAINTS THE BAND. The design is a dark band, which every caller
 * paints by default, and an operator's section theme overrides it (`base-100`
 * is a real choice). The band belongs to the section, not to this component, so
 * nothing here names its colours: the arrows, the rail and the counter are all
 * drawn from `current`, the section's text colour, and read on whichever band
 * they land on. The comparison's divider and pills stay light: they sit on the
 * photo, not the band.
 *
 * FROM `lg` IT IS TWO COLUMNS: the photo and its plate in the left two thirds,
 * and a side column in the right third holding the heading, the index rail as
 * a vertical list, and the arrows. With a full-width 16:9 photo the section ran
 * ~1,240px tall at every desktop width, taller than any laptop shows at once,
 * so the heading, the photo, its caption and the controls were never on screen
 * together. Side by side it is ~670px at 1440 wide. Below `lg` it is the
 * stacked layout it has always been: heading and arrows, photo, plate, rail.
 *
 * The rail and the arrows are rendered ONCE PER LAYOUT, each copy
 * `display: none` at the other's widths, so the DOM order is the visual order
 * at every width; {@link ShowcaseRail} says why that beats moving one copy with
 * CSS. The heading is rendered once: the side column is an ordinary block
 * below `lg`, where it holds only the heading row.
 *
 * A section with NO heading (a market's, left blank, has none to fall back to)
 * drops the heading row from the side column, so the rail starts level with the
 * photo; with one item as well there is no side column at all, and the photo
 * centres at the same two-thirds width instead of sitting beside an empty third.
 * Callers pass `header={null}` for that, not an empty wrapper.
 *
 * The caller passes the photo's `sizes`, derived from the container it renders
 * this in (`showcaseImageSizesFor`), because only the caller knows that box.
 *
 * THE CLIENT BOUNDARY OF THIS SECTION. The heading above it is server-rendered
 * and passed in as `header`, so the only JavaScript this style ships is the
 * rail's active index, the per-item divider positions and the markup that reads
 * them. Every item's copy and photo is in the served HTML whatever the visitor
 * does, and JavaScript only chooses which one is on screen — see
 * {@link ShowcaseSlide} for why that is not the same as rendering the active
 * item alone.
 *
 * `activeIndex` is CLAMPED at read time rather than reconciled in an effect.
 * The item list is a server prop and can shrink between renders (an editor
 * unpublishes a job while the page is open in a stale tab); clamping on read
 * means the swap is a plain render with no intermediate frame pointing past the
 * end of the array.
 */
export const PortfolioShowcase: React.FC<{
  items: PortfolioShowcaseItem[];
  /** The server-rendered heading, or `null` when the section has none. */
  header: React.ReactNode;
  /** The photo's `sizes`, from `showcaseImageSizesFor(<the caller's container class>)`. */
  imageSizes: string;
  /**
   * The caller's "View all our work" link (`PortfolioIndexViewAllLink`), which
   * renders nothing while the tenant's portfolio index is off. Placed once per
   * layout: at the foot of the side column from `lg`, under the row rail below.
   */
  viewAll?: React.ReactNode;
  /**
   * Both default ON, because both are what the design shows. They exist as props
   * for the market block, which puts an operator toggle on each; the homepage
   * has no such field and omits them.
   */
  showQuotes?: boolean;
  showPlace?: boolean;
  /**
   * The Subjects from the server caller (`portfolioSubjects`): each item's by
   * item id, spread on its slide, and each photo's by Media id.
   */
  subjects?: SubjectsById;
}> = ({ items, header, imageSizes, showQuotes = true, showPlace = true, viewAll, subjects }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { pctOf, setPct } = usePortfolioSliders();
  const railRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const slideFits = useSlideFitsViewport(layoutRef, activeIndex, items);

  useRecentreRailWhenShown(railRef, Math.min(activeIndex, items.length - 1), items);

  /**
   * NOTHING TO SHOW IS AN EMPTY SECTION, NOT A SECTION SHELL. Without this the
   * clamp below resolves an empty list to index -1, every guard downstream
   * holds, and the render is a heading row and a dark band wrapped around
   * nothing — a band the caller has already painted.
   *
   * Both callers today guard before rendering this, so the branch is a BACKSTOP
   * rather than a live path; it exists because this component moved into
   * `shared/` and the next caller has no way to know the guard is theirs to
   * keep. `PortfolioItemGrid` next door returns null on empty for the same
   * reason. Placed AFTER the hooks so the hook order stays unconditional.
   */
  if (items.length === 0) { return null; }

  const index = Math.min(activeIndex, items.length - 1);
  const last = items.length - 1;

  /**
   * The CLAMP is the ONLY guard; `aria-disabled` on the end arrow is
   * presentation. Unlike a native `disabled`, it does not stop the click, so a
   * press at either end is a live path, not a hypothetical one — it reaches here,
   * resolves to the item already showing, and returns. The early return is what
   * makes it a true no-op: without it, the press would still re-centre the rail,
   * yanking back a rail the visitor had scrolled by hand. Both are set from the
   * same clamped `index`, so they cannot disagree.
   */
  const step = (dir: 1 | -1) => {
    const next = Math.max(0, Math.min(last, index + dir));

    if (next === index) { return; }

    setActiveIndex(next);
    scrollRailTo(railRef.current, next);
  };

  /**
   * ONE item is neither a rail nor a stepper. With nothing to step to, both
   * arrows would be permanently disabled and the rail a single full-width
   * button that changes nothing when pressed — controls that lie about being
   * ones.
   */
  const isSteppable = items.length > 1;
  const hasHeader = header !== null && header !== undefined;

  /** Nothing to put beside the photo: no heading and nothing to step through. */
  const hasSide = hasHeader || isSteppable;

  const arrows = (
    <StripArrows
      prevLabel="Previous work"
      nextLabel="Next work"
      canPrev={index > 0}
      canNext={index < last}
      onStep={step}
    />
  );

  return (
    <div ref={layoutRef} className={cn(hasSide && 'lg:grid lg:grid-cols-3 lg:gap-12')}>
      {/*
        THE SIDE COLUMN: the right-hand third from `lg`. Below `lg` it is an
        ordinary block holding only the heading row, because its list and its
        arrows are the `lg` copies and are `display: none` there.
      */}
      {!hasSide ? null : (
        <div className="lg:col-start-3 lg:row-start-1 lg:flex lg:flex-col">
          <div
            className={cn(
              'flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between',
              // Below `sm` the arrow row is the photo's toolbar, so it stands tight
              // on the photo; with no arrows, the heading keeps the full gap. From
              // `lg` the row holds the heading alone, and `sm:mb-10` is its gap to
              // the rail. With no heading it holds only the phone arrows, so from
              // `lg` it goes altogether and the rail starts level with the photo.
              isSteppable ? 'mb-4 sm:mb-10' : 'mb-10',
              !hasHeader && 'lg:hidden',
            )}
          >
            {/*
              THE HEADER SLOT HAS TO BE TOLD TO GROW. `SectionHeading` — what every
              caller puts in here — is a `@container`, and `container-type:
              inline-size` applies inline-axis containment: the element's intrinsic
              contribution is ZERO, so as a bare flex item it resolves to a 0px-wide
              box whose text spills out one word per line. `grow` gives it the row's
              free space instead, which its own `max-w-*` then caps; `min-w-0` keeps
              a long unbroken word from pushing the arrows off the end. The same
              containment is what steps the heading's type down in the side column
              from `lg`, where the slot is a third of the band wide.
            */}
            {!hasHeader ? null : <div className="min-w-0 grow">{header}</div>}

            {/*
              Shown at EVERY width below `lg`, matching the filmstrip's pair, and
              replaced from `lg` by the side column's copy under the rail. A swipe
              is not the fallback here that it is there: this photo does not
              scroll at any width, so these are the ONLY way through the work for
              everyone the rail's own scroll does not reach.

              BELOW `sm` THE ROW IS THE PHOTO'S TOOLBAR. The header stacks there, so
              the pair gets a row of its own, and, left-aligned with the header's
              full gap on both sides, that row belonged to neither the heading
              above it nor the photo below. So it spans the column, puts the pair
              on the RIGHT, and stands 16px off the photo (the `mb-4` above), which
              is what makes it read as the photo's controls. Above `sm` the row is
              the heading's and the pair stands alone at its end, as it always has;
              with no heading, `sm:ml-auto` keeps it at that end.

              The left of that phone row carries a position counter. On a phone the
              rail, the section's other readout of where the visitor is, sits a
              photo and a full plate further down, below the fold, so without it
              the arrows step through the jobs with nothing on screen saying which
              one is showing. It is `aria-hidden` because it is a visual stand-in
              for the rail, whose `aria-current` already tells assistive technology
              the same thing at every width.
            */}
            {!isSteppable ? null : (
              <Reveal stagger={1} className={cn('lg:hidden', !hasHeader && 'sm:ml-auto')}>
                <div className="flex items-center justify-between gap-4">
                  {/*
                    The total dims with `opacity` on its own span: a dimmed
                    colour on the paragraph would dim the current number too,
                    and the section colour to restore it to is not nameable here.
                    80%, for the contrast reason `ShowcaseRail` gives.
                  */}
                  <p aria-hidden="true" className="font-mono text-xs tracking-widest sm:hidden">
                    <span>{pad(index + 1)}</span><span className="opacity-80"> / {pad(items.length)}</span>
                  </p>

                  {arrows}
                </div>
              </Reveal>
            )}
          </div>

          {!isSteppable ? null : (
            <>
              <Reveal stagger={2} cover={40} className="hidden lg:block">
                <ShowcaseRail items={items} index={index} onSelect={setActiveIndex} orientation="column" />
              </Reveal>

              <Reveal stagger={3} className="mt-8 hidden lg:block">
                {arrows}
              </Reveal>
            </>
          )}

          {/*
            "View all our work" closes the index column from `lg`: the rest of
            the work, after the list of what is here. Its own top margin; this
            wrapper only picks the width it shows at.
          */}
          {viewAll === undefined ? null : <div className="hidden lg:block">{viewAll}</div>}
        </div>
      )}

      {items.map((item, i) => (
        <ShowcaseSlide
          key={item.id}
          item={item}
          hidden={i !== index}
          showQuotes={showQuotes}
          showPlace={showPlace}
          pct={pctOf(item.id)}
          onPct={setPct}
          sizes={imageSizes}
          centred={!hasSide}
          sticky={slideFits}
          placeholder={i === 0}
          subject={subjects?.[item.id]}
          subjects={subjects}
        />
      ))}

      {!isSteppable ? null : (
        <Reveal stagger={2} cover={40} className="mt-14 lg:hidden">
          <ShowcaseRail
            items={items}
            index={index}
            onSelect={setActiveIndex}
            orientation="row"
            scrollerRef={railRef}
          />
        </Reveal>
      )}

      {/*
        Below `lg` the link is the section's last line, under the row rail. With
        no side column there is no `lg` copy, so this one shows at every width,
        in the centred photo's own box so it lines up under it.
      */}
      {viewAll === undefined ? null : <div className={hasSide ? 'lg:hidden' : CENTRED_SLIDE_CLASS}>{viewAll}</div>}
    </div>
  );
};
